import { Fragment, useEffect, useMemo } from 'react';
import { COLLECTIONS } from '~/assets/collections';
import { CollectionImage } from '~/components/collections';
import { PanelControls } from '~/components/ui/PanelControls';
import { rage, useInputAction, useObservable } from '~/rage';
import {
	IModalReplyDefault,
	InputAction,
	InputKey,
	ModalButtonColor,
	ModalType,
	VehicleTuningCategory,
	WalletType,
} from '~/shared';
import { useBuy, useTuningCategories } from '../../hooks';
import {
	resetActiveVariants,
	setActiveIndex,
	setActiveIndexNext,
	setActiveIndexPrev,
	setActiveStage,
	setActiveTuningCategory,
	setActiveTuningGroup,
	setActiveTuningSubcategory,
	setActiveVariant,
	setMaxIndex,
	TuningStudioStage,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import {
	SUBCATEGORY_TO_GROUP,
	TUNING_GROUP_SUBCATEGORIES,
	TUNING_SUBCATEGORY_ICONS,
	TUNING_SUBCATEGORY_LABELS,
} from '../../tuningGroups.model';
import {
	getActiveVariant,
	getEffectiveTuningSelection,
} from '../../utils/resolveTuningShopVariant';
import { Categories } from '../Categories';
import { CATEGORY_ICONS } from '../Main/Main.model';
import { MenuItem } from '../MenuItem';
import {
	StyledBottomContainer,
	StyledControlsContainer,
} from './Tuning.styled';

const Tuning = () => {
	const dispatch = useLayoutDispatch();
	const activeTuningCategory = useLayoutSelector(
		(state) => state.tuningStudio.activeTuningCategory,
	);
	const activeTuningSubcategory = useLayoutSelector(
		(state) => state.tuningStudio.activeTuningSubcategory,
	);
	const { shopItems, shopCategories, nonExteriorCategories } =
		useTuningCategories();
	const ownedFreeTuning = useObservable(
		rage.vehicleTuningShop.getOwnedFreeTuning(),
	);
	const donateItems = useObservable(rage.inventoryDonate.getDonateOwnedItems());
	const activeIndex = useLayoutSelector(
		(state) => state.tuningStudio.activeIndex,
	);
	const modalState = useObservable(rage.modal.getCurrentModal());
	const interfaceHidden = useLayoutSelector(
		(state) => state.tuningStudio.interfaceHidden,
	);
	const activeVariants = useLayoutSelector(
		(state) => state.tuningStudio.activeVariants,
	);
	const { handleBuy } = useBuy();

	const checkIsOwned = (itemId: string): boolean => {
		if (rage.vehicleTuningShop.getIsOwned(itemId)) {
			return true;
		}
		if (!ownedFreeTuning) return false;
		return (
			ownedFreeTuning.ownedItems.includes(itemId) ||
			ownedFreeTuning.ownedKits.includes(itemId)
		);
	};

	const getDonateCount = (itemId: string): number => {
		return donateItems.find((item) => item.id === itemId)?.count ?? 0;
	};

	const handleInstallOwned = (
		itemId: string,
		itemName: string,
		isKit: boolean,
	) => {
		if (modalState || interfaceHidden) {
			return;
		}

		const text = `Вы хотите установить "${itemName}" на ваш автомобиль?`;

		rage.modal.showModal(
			{
				type: ModalType.Default,
				title: 'Подтверждение',
				text: text,
				acceptButton: {
					text: 'Установить',
					color: ModalButtonColor.Success,
				},
				cancelButton: {
					text: 'Отменить',
					color: ModalButtonColor.Secondary,
				},
			},
			(result: IModalReplyDefault) => {
				if (result.isAccepted) {
					if (isKit) {
						rage.vehicleTuningShop.installOwnedKit(itemId);
					} else {
						rage.vehicleTuningShop.installOwnedItem(itemId);
					}
				}
			},
		);
	};

	const headerName = useMemo(() => {
		if (activeTuningSubcategory) {
			return TUNING_SUBCATEGORY_LABELS[activeTuningSubcategory] ?? '';
		}
		if (activeTuningCategory) {
			return (
				shopCategories.find((c) => c.id === activeTuningCategory)?.name ?? ''
			);
		}
		return '';
	}, [activeTuningSubcategory, activeTuningCategory, shopCategories]);

	const filteredShopItems = useMemo(() => {
		if (activeTuningSubcategory) {
			return shopItems.filter(
				(item) => item.subcategoryId === activeTuningSubcategory,
			);
		}
		if (activeTuningCategory) {
			return shopItems.filter(
				(item) => item.categoryId === activeTuningCategory,
			);
		}
		return [];
	}, [shopItems, activeTuningCategory, activeTuningSubcategory]);

	const leftControls = useMemo(() => {
		const activeItem = filteredShopItems[activeIndex];
		const effective = activeItem
			? getEffectiveTuningSelection(activeItem, activeVariants)
			: null;
		const isOwned =
			!!effective &&
			(getDonateCount(effective.id) > 0 || checkIsOwned(effective.id));
		const isKit = activeItem?.categoryId === VehicleTuningCategory.KITS;

		const button =
			isOwned && effective
				? {
						text: 'Установить',
						keys: [InputKey.Enter],
						onClick: () =>
							handleInstallOwned(effective.id, effective.installLabel, isKit),
				  }
				: {
						text: 'Купить',
						keys: [InputKey.Enter],
						onClick: () => handleBuy(WalletType.Cash),
				  };

		return [
			{
				text: 'Управление',
				keys: [InputKey.ArrowLeft, InputKey.ArrowRight],
			},
			...(activeItem?.unavailable ? [] : [button]),
			{
				text: 'Крутить камеру',
				keys: [InputKey.MouseRight],
			},
		];
	}, [
		handleBuy,
		handleInstallOwned,
		filteredShopItems,
		activeIndex,
		ownedFreeTuning,
		activeVariants,
	]);

	const rightControls = [
		{
			text: 'Выйти',
			keys: ['Esc'],
			onClick: () => rage.vehicleTuningShop.exitMenu(),
		},
	];

	const handleBack = () => {
		const category = activeTuningCategory;

		const resetPreview = async () => {
			try {
				if (category === VehicleTuningCategory.KITS) {
					await rage.vehicleTuningShop.installItemsKit(null);
				} else if (category) {
					await rage.vehicleTuningShop.installItem(null);
				}
				await rage.vehicleTuningShop.removeTemporaryItems();
				await rage.vehicleTuningShop.resetWheelsSettings();
			} catch (err) {
				rage.notifications.showException(err);
			}
		};

		void resetPreview();

		dispatch(resetActiveVariants());

		const group = activeTuningSubcategory
			? SUBCATEGORY_TO_GROUP[activeTuningSubcategory]
			: undefined;
		if (group) {
			const hasMultipleCats = TUNING_GROUP_SUBCATEGORIES[group].length > 1;
			dispatch(setActiveTuningCategory(null));
			dispatch(setActiveTuningSubcategory(null));
			if (hasMultipleCats) {
				dispatch(setActiveTuningGroup(group));
				dispatch(setActiveStage(TuningStudioStage.SUBCATEGORY_MENU));
			} else {
				dispatch(setActiveTuningGroup(null));
				dispatch(setActiveStage(TuningStudioStage.EXTERIOR_MENU));
			}
			dispatch(setActiveIndex(0));
			return;
		}

		const parentIndex = activeTuningCategory
			? nonExteriorCategories.findIndex((c) => c.id === activeTuningCategory)
			: -1;
		if (activeTuningCategory === VehicleTuningCategory.KITS) {
			dispatch(setActiveStage(TuningStudioStage.EXTERIOR_MENU));
			dispatch(setActiveIndex(0));
		} else {
			dispatch(setActiveStage(TuningStudioStage.MAIN));
			dispatch(setActiveIndex(parentIndex >= 0 ? parentIndex + 3 : 0));
		}
		dispatch(setActiveTuningCategory(null));
		dispatch(setActiveTuningSubcategory(null));
	};

	useInputAction(InputAction.CommonMenuNavLeft, () => {
		if (modalState || interfaceHidden) {
			return;
		}
		dispatch(setActiveIndexPrev());
	});

	useInputAction(InputAction.CommonMenuNavRight, () => {
		if (modalState || interfaceHidden) {
			return;
		}
		dispatch(setActiveIndexNext());
	});

	useInputAction(InputAction.CommonMenuAccept, () => {
		if (modalState || interfaceHidden) {
			return;
		}

		const activeItem = filteredShopItems[activeIndex];
		if (!activeItem || activeItem.unavailable) {
			return;
		}

		const effective = getEffectiveTuningSelection(activeItem, activeVariants);
		const isOwned =
			getDonateCount(effective.id) > 0 || checkIsOwned(effective.id);
		if (isOwned) {
			const isKit = activeItem.categoryId === VehicleTuningCategory.KITS;
			handleInstallOwned(effective.id, effective.installLabel, isKit);
			return;
		}

		handleBuy(WalletType.Cash);
	});

	useInputAction(InputAction.CommonMenuClose, () => {
		if (modalState || interfaceHidden) {
			return;
		}
		handleBack();
	});

	useEffect(() => {
		dispatch(setActiveIndex(0));
	}, [activeTuningCategory, activeTuningSubcategory, dispatch]);

	useEffect(() => {
		const maxIndex = Math.max(0, filteredShopItems.length - 1);
		dispatch(setMaxIndex(maxIndex));
	}, [filteredShopItems, dispatch]);

	useEffect(() => {
		if (!filteredShopItems.length) {
			return;
		}

		const safeIndex = Math.min(activeIndex, filteredShopItems.length - 1);
		const activeItem = filteredShopItems[safeIndex];
		if (!activeItem) {
			return;
		}

		if (
			activeItem.variants &&
			activeItem.variants.length > 0 &&
			activeVariants[activeItem.id] === undefined
		) {
			dispatch(setActiveVariant({ itemId: activeItem.id, index: 0 }));
			return;
		}

		if (activeItem.categoryId === VehicleTuningCategory.KITS) {
			rage.vehicleTuningShop
				.installItemsKit(activeItem.id)
				.catch((err) => rage.notifications.showException(err));
			return;
		}

		const { id: installId } = getEffectiveTuningSelection(
			activeItem,
			activeVariants,
		);
		if (!installId) {
			return;
		}

		rage.vehicleTuningShop
			.installItem(installId)
			.catch((err) => rage.notifications.showException(err));
	}, [filteredShopItems, activeIndex, activeVariants, dispatch]);

	const resolveItemIcon = (
		item: (typeof filteredShopItems)[number],
	): string => {
		return (
			(item.subcategoryId
				? TUNING_SUBCATEGORY_ICONS[item.subcategoryId]
				: undefined) ?? CATEGORY_ICONS[item.categoryId]
		);
	};

	return (
		<Fragment>
			<StyledBottomContainer>
				<Categories
					name={headerName}
					activeIndex={activeIndex}
					totalCount={filteredShopItems.length}
					handleBack={handleBack}
					handlePrev={() => dispatch(setActiveIndexPrev())}
					handleNext={() => dispatch(setActiveIndexNext())}
				>
					{filteredShopItems.map((item, index) => {
						const effective = getEffectiveTuningSelection(item, activeVariants);
						const variantLen = item.variants?.length ?? 0;
						const hasVariants =
							item.categoryId !== VehicleTuningCategory.KITS && variantLen > 1;
						const vIdx = hasVariants
							? Math.min(
									Math.max(0, activeVariants[item.id] ?? 0),
									variantLen - 1,
							  )
							: 0;
						const itemDisplayName = effective.parentName;
						const activeVariant = getActiveVariant(item, activeVariants);
						const isInstalled = activeVariant
							? !!activeVariant.isInstalled
							: !!item.isInstalled;

						const isRim = item.categoryId === VehicleTuningCategory.RIMS;
						const isStock = item.id.startsWith('stock_');
						const imageSlot =
							isRim && !isStock ? (
								<CollectionImage
									collection="rims"
									id={
										item.id as (typeof COLLECTIONS)['rims']['contents'][number]
									}
									size={120}
								/>
							) : undefined;

						return (
							<MenuItem
								key={item.id}
								type="item"
								name={itemDisplayName}
								icon={resolveItemIcon(item)}
								imageSlot={imageSlot}
								active={activeIndex === index}
								unavailable={item.unavailable}
								installed={isInstalled}
								paintType={item.paintType}
								onClick={() => dispatch(setActiveIndex(index))}
								price={effective.price}
								id={`tuningItem#${index}`}
								stock={
									getDonateCount(effective.id) ||
									(checkIsOwned(effective.id) ? 1 : 0)
								}
								variantsCount={hasVariants ? variantLen : 0}
								variantIndex={vIdx}
								onPrevVariant={
									hasVariants
										? () => {
												const cur = activeVariants[item.id] ?? 0;
												dispatch(
													setActiveVariant({
														itemId: item.id,
														index: cur === 0 ? variantLen - 1 : cur - 1,
													}),
												);
										  }
										: undefined
								}
								onNextVariant={
									hasVariants
										? () => {
												const cur = activeVariants[item.id] ?? 0;
												dispatch(
													setActiveVariant({
														itemId: item.id,
														index: cur === variantLen - 1 ? 0 : cur + 1,
													}),
												);
										  }
										: undefined
								}
							/>
						);
					})}
				</Categories>
			</StyledBottomContainer>
			<StyledControlsContainer>
				<PanelControls controls={leftControls} align="left" />
				<PanelControls controls={rightControls} align="right" />
			</StyledControlsContainer>
		</Fragment>
	);
};

export { Tuning };
